<script setup lang="ts">
import { reactive, ref, Ref } from 'vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { useClickOutside } from '@/composables/clickOutside';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CInput from '@/components/shared/CInput.vue';
import CFileInput from '@/components/shared/CFileInput/CFileInput.vue';
import ColorSelector from './ColorSelector.vue';
import PreviewColorExample from '@/assets/images/preview-bg-color-example.png';
import PreviewImageExample from '@/assets/images/preview-img-example.png';
import type { FontColorOption } from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';

const DEFAULT_COLOR = '#EDE9FB';

const validation = {
    rules: 'required|min_value:600|max_value:1200',
    messages: {
        min_value: "The value is too small and it won't look good in your store",
        max_value: 'Use a smaller value, big values can result in bad performance and slow loading times on your store'
    }
};

interface CanvasBackground {
    imgFile?: File;
    width?: number;
    height?: number;
    bgColor?: string;
}

const props = defineProps<{
    background?: CanvasBackground;
}>();

const emit = defineEmits<{
    (event: 'set-background', value: CanvasBackground): void;
}>();

const model = reactive({
    files: [] as File[],
    height: props.background?.height || 1000,
    width: props.background?.width || 1000,
    color: {
        hex: props.background?.bgColor || DEFAULT_COLOR,
        alpha: 1,
        cmyk: ''
    } as ColorObject
});

const pickerRef: Ref<HTMLElement | undefined> = ref();
const { clickOutsideActive } = useClickOutside(pickerRef, () => toggleColorSelection(), false);
const toggleColorSelection = () => {
    clickOutsideActive.value = !clickOutsideActive.value;
};

const onBgColorSelected = (color: FontColorOption) => {
    model.color = { ...color } as ColorObject;
    toggleColorSelection();
};

const setBgColor = () => {
    setBackground({
        width: model.width,
        height: model.height,
        bgColor: model.color.hex
    });
};

const onImageUploaded = () => {
    setBackground({ imgFile: model.files[0] });
};

const setBackground = (background: CanvasBackground) => {
    emit('set-background', { ...background });
};
</script>

<template>
    <c-modal
        :value="true"
        hide-footer
        hide-close
        :scrollable="false"
        size="lg"
        title="Define a Background for your Preview"
    >
        <div class="bg-mode-container">
            <div class="bg-mode">
                <img :src="PreviewImageExample" alt="image-example" height="350" width="350" />
                <h6 class="title">Upload Background Image</h6>
                <p>
                    The preview size will adapt to your image's aspect ratio. Upload images up to 1200px for best
                    performance on your store. Large images will be scaled down automatically.
                </p>

                <c-file-input
                    v-model="model.files"
                    class="mt-auto"
                    :display-preview="false"
                    accept=".png,.jpg,.jpeg,.webp"
                    button-text="Upload Image"
                    @input="onImageUploaded"
                >
                    <template #uploadTrigger="{ onFileUpload }">
                        <c-button class="flex-fill" variant="primary" text="Upload Image" @click="onFileUpload" />
                    </template>
                </c-file-input>
            </div>
            <div class="bg-mode">
                <img
                    :src="PreviewColorExample"
                    alt="color-example"
                    height="350"
                    width="350"
                    :style="{ backgroundColor: model.color?.hex || DEFAULT_COLOR }"
                />
                <h6 class="title">Select Background Color</h6>
                <p>Choose any color and set your preview size by entering width and height (in px).</p>
                <color-selector
                    ref="pickerRef"
                    class="color-selector"
                    :open="clickOutsideActive"
                    :color="model.color"
                    @cancel="toggleColorSelection"
                    @color-selected="onBgColorSelected"
                />
                <div
                    class="color-preview rounded"
                    :style="{ backgroundColor: model.color?.hex || DEFAULT_COLOR }"
                    @click="toggleColorSelection"
                >
                    <font-awesome-icon class="edit-icon" :icon="['fas', 'pen']" />
                </div>
                <validation-observer v-slot="{ invalid }" tag="div">
                    <div class="d-flex">
                        <validation-provider
                            v-slot="{ failed, errors }"
                            slim
                            :rules="validation.rules"
                            :custom-messages="validation.messages"
                        >
                            <c-input
                                v-model="model.width"
                                class="flex-fill mr-2"
                                label="Width (px)"
                                type="decimal"
                                :min="1"
                                :step="1"
                                :error="failed"
                                :error-msg="errors[0]"
                            />
                        </validation-provider>
                        <validation-provider
                            v-slot="{ failed, errors }"
                            slim
                            :rules="validation.rules"
                            :custom-messages="validation.messages"
                        >
                            <c-input
                                v-model="model.height"
                                class="flex-fill"
                                label="Height (px)"
                                type="decimal"
                                :min="1"
                                :step="1"
                                :error="failed"
                                :error-msg="errors[0]"
                            />
                        </validation-provider>
                    </div>
                    <c-button
                        class="w-100 mt-2"
                        variant="primary"
                        text="Set Background Color"
                        :disabled="invalid"
                        @click="setBgColor"
                    />
                </validation-observer>
            </div>
        </div>
    </c-modal>
</template>

<style scoped lang="scss">
@import 'styles/utilities/functions';
.bg-mode-container {
    display: flex;
    gap: 1.25rem;
    .bg-mode {
        display: flex;
        flex-direction: column;
        flex: 1;
        border-radius: 0.5rem;
        border: 1px solid get-color('gray-light');
        padding: 0.625rem;
        gap: 0.625rem;
        font-size: 0.75rem;
        img {
            margin: 0 auto;
        }
        .title {
            margin: 0;
            font-weight: bold;
            font-size: 1rem;
        }
        p {
            margin: 0;
        }
        .color-selector {
            position: absolute;
            bottom: 9rem;
            left: -3rem;
        }
        .color-preview {
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            height: 2rem;
            color: get-color('white');
            .edit-icon {
                visibility: hidden;
            }
            &:hover {
                .edit-icon {
                    visibility: visible;
                }
            }
        }
    }
}
</style>
